import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Menu, PenLine, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGetAgentById } from '@/modules/gpt-chats/hooks/use-agents';
import { AgentsConfiguration } from '../../components/agents-configuration/agents-configuration';
import { AgentConversation } from '../agent-conversations/agent-conversations';
import { EditAgentModal } from '@/modules/gpt-chats/components/agents-configuration/components/edit-agent-modal/edit-agent-modal';
import { Button } from '@/components/ui-kit/button';
import { Badge } from '@/components/ui-kit/badge';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui-kit/tooltip';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui-kit/breadcrumb';
import emptyProfilePhoto from '@/assets/images/widget.png';
import { AgentIntegrationHub } from '../agent-integrations-hub';
import { AgentsRetrievalTesting } from '../../components/agents-retrieval-testing';

export const AgentDetailsPage = () => {
  const { t } = useTranslation();
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data } = useGetAgentById(id || '');

  useEffect(() => {
    if (id && !tab) {
      navigate(`/ai/agent-details/${id}/configuration`, { replace: true });
    }
  }, [id, tab, navigate]);

  if (!id) {
    return null;
  }

  const agentName = data?.agent?.name || t('LOADING_AGENT');
  const logoUrl = data?.agent?.logo_url;

  const sidebarOptions = [
    { id: 'configuration', label: t('CONFIGURATION') },
    { id: 'retrieval-testing', label: t('RETRIEVAL_TESTING') },
    // { id: 'integrations', label: t('INTEGRATIONS') },
    // { id: 'conversations', label: t('CONVERSATIONS') },
  ];

  const currentTab = tab || 'configuration';

  const formatTabName = (tabId: string) => {
    return sidebarOptions.find((opt) => opt.id === tabId)?.label || t('CONFIGURATION');
  };

  return (
    <div className="flex gap-6 min-h-[calc(100vh-120px)] w-full items-stretch">
      {/* Sidebar Component */}
      <div
        className={cn(
          'flex flex-col bg-card border border-border rounded-xl shadow-sm transition-all duration-300 ease-in-out overflow-hidden shrink-0 self-stretch',
          isSidebarOpen ? 'w-72 p-5' : 'w-0 p-0 border-none opacity-0'
        )}
      >
        {/* Sidebar Header */}
        <div className="flex items-center gap-3 pb-4 border-b border-border mb-5 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 cursor-pointer hover:bg-accent shrink-0"
            onClick={() => setIsSidebarOpen(false)}
          >
            <Menu size={20} />
          </Button>
          <span className="font-semibold text-lg text-foreground truncate">{t('AI_AGENT')}</span>
        </div>

        {/* Agent Info section */}
        <div className="flex flex-col gap-3 pb-5 border-b border-border mb-5 shrink-0">
          <div className="flex items-center gap-4">
            <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-muted/30 border border-border flex items-center justify-center">
              <img
                src={logoUrl || emptyProfilePhoto}
                alt={agentName}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex flex-col gap-1.5 min-w-0 flex-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <h4 className="font-semibold text-base text-foreground truncate" title={agentName}>
                  {agentName}
                </h4>
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="text-primary hover:text-primary/80 transition-colors cursor-pointer shrink-0"
                  title={t('EDIT_AGENT')}
                >
                  <PenLine size={16} />
                </button>
              </div>
              {data?.agent && (
                <Badge variant="outline" className="w-fit bg-border text-[12px] px-2 py-0.5">
                  {!data.agent.is_any_agent
                    ? t('AGENT_TYPE_SEQUENTIAL_NAME')
                    : t('AGENT_TYPE_REASONING_NAME')}{' '}
                  {t('AGENT')}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="flex flex-col gap-1.5 shrink-0">
          {sidebarOptions.map((opt) => {
            const isActive = currentTab === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => navigate(`/ai/agent-details/${id}/${opt.id}`)}
                className={cn(
                  'flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-left cursor-pointer',
                  isActive
                    ? 'bg-primary/10 text-primary font-semibold shadow-sm'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header Row with Breadcrumb & Collapse Trigger */}
        <div className="flex items-center mb-6 min-h-10">
          {!isSidebarOpen && (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 cursor-pointer mr-3 hover:bg-accent border border-border shrink-0"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu size={20} />
            </Button>
          )}

          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to="/ai/agents"
                    className="text-foreground font-medium hover:text-primary transition-colors"
                  >
                    {t('AI_AGENTS')}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-muted-foreground font-medium">
                  {formatTabName(currentTab)}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {currentTab !== 'configuration' && (
          <div className="flex items-center gap-4 mb-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-5 cursor-pointer"
                  onClick={() => {
                    queryClient.invalidateQueries({ queryKey: ['agent-conversation-list'] });
                    queryClient.invalidateQueries({ queryKey: ['conversations'] });
                    navigate('/ai/agents');
                  }}
                >
                  <ArrowLeft className="text-primary" size={20} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('BACK_TO_AGENTS')}</p>
              </TooltipContent>
            </Tooltip>
            <h3 className="text-2xl font-bold tracking-tight">{formatTabName(currentTab)}</h3>
          </div>
        )}

        {/* Render Tab Contents */}
        <div className="flex-1 min-h-0">
          {currentTab === 'configuration' && <AgentsConfiguration agentId={id} />}

          {currentTab === 'retrieval-testing' && (
            // <div className="flex h-[400px] flex-col items-center justify-center rounded-xl border border-dashed border-border p-8 text-center bg-card">
            //   <h3 className="text-xl font-semibold mb-2 text-foreground">Retrieval Testing</h3>
            //   <p className="text-muted-foreground max-w-sm">
            //     This page is under construction. We will add retrieval testing tools here soon.
            //   </p>
            // </div>
            <AgentsRetrievalTesting agentId={id} />
          )}

          {currentTab === 'integrations' && (
            // <div className="flex h-[400px] flex-col items-center justify-center rounded-xl border border-dashed border-border p-8 text-center bg-card">
            //   <h3 className="text-xl font-semibold mb-2 text-foreground">Integrations</h3>
            //   <p className="text-muted-foreground max-w-sm">
            //     This page is under construction. We will add integration options here soon.
            //   </p>
            // </div>
            <AgentIntegrationHub id={id} />
          )}

          {currentTab === 'conversations' && id && <AgentConversation id={id} />}
        </div>
      </div>

      {/* Edit Agent Modal */}
      <EditAgentModal agentId={id} open={isEditModalOpen} onOpenChange={setIsEditModalOpen} />
    </div>
  );
};
